import { useState, useMemo, useCallback } from "react";
import { Plus, Edit2, Trash } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { ProColumns, ProTable } from "@ant-design/pro-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Popconfirm, Checkbox, Switch, Tag, Select } from "antd";
import CustomButton from "@/components/common/Button";
import { notify } from "@/components/common/Notification";
import { TenantPermissionService, DropdownService } from "@/services/APIs";
import RolePermissionModal from "@/project/PTS/pages/RolesPermissions/components/RolePermissionModal";
import { filterByLabel } from "@/lib/filterByLabel";

interface RolePermission {
  id: string;
  role?: string;
  role_name?: string;
  schema_name?: string;
  level?: number;
  only_if_created?: boolean;
  is_active?: boolean;
  can_select?: boolean;
  can_read?: boolean;
  can_write?: boolean;
  can_create?: boolean;
  can_delete?: boolean;
  can_print?: boolean;
  can_export?: boolean;
  can_import?: boolean;
  can_email?: boolean;
  can_report?: boolean;
  can_share?: boolean;
  name?: string;
  description?: string;
}

const RolePermissionManager = () => {
  const queryClient = useQueryClient();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPermission, setEditingPermission] =
    useState<RolePermission | null>(null);
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<
    string | undefined
  >(undefined);
  const [selectedSchemaFilter, setSelectedSchemaFilter] = useState<
    string | undefined
  >(undefined);

  // Fetch permissions
  const { data: permissionsResponse, isLoading: permissionsLoading } = useQuery(
    {
      queryKey: ["role-permissions"],
      queryFn: () => TenantPermissionService.listPermissions(1, 1000),
    }
  );

  // Fetch role options for display
  const { data: roleOptions } = useQuery({
    queryKey: ["role"],
    queryFn: () => DropdownService.getDropdownData("TenantRole"),
  });

  // Fetch schema options for display with PTS filter
  const { data: schemaOptions } = useQuery({
    queryKey: ["schema", "PTS"],
    queryFn: () =>
      DropdownService.getDropdownData("schema", { name__ilike: "%PTS%" }),
  });

  // Helper to get role name by ID
  const getRoleName = useCallback(
    (roleId: string | undefined) => {
      if (!roleId) {
        return "-";
      }
      const role = roleOptions?.find(
        (r: { value: string; label: string }) => r.value === roleId
      );
      return role?.label ?? roleId;
    },
    [roleOptions]
  );

  // Helper to get schema name
  const getSchemaName = useCallback(
    (schemaName: string | undefined) => {
      if (!schemaName) {
        return "-";
      }
      const schema = schemaOptions?.find(
        (s: { value: string; label: string }) =>
          s.value === schemaName || s.label === schemaName
      );
      return schema?.label ?? schemaName;
    },
    [schemaOptions]
  );

  const handleAddPermission = () => {
    setEditingPermission(null);
    setModalOpen(true);
  };

  const handleEditPermission = useCallback((permission: RolePermission) => {
    setEditingPermission(permission);
    setModalOpen(true);
  }, []);

  const handleDeletePermission = useCallback(
    async (id: string) => {
      try {
        await TenantPermissionService.deletePermission(id);
        notify.success("Permission deleted successfully");
        queryClient.invalidateQueries({ queryKey: ["role-permissions"] });
      } catch (error: any) {
        notify.error(error?.message ?? "Failed to delete permission");
      }
    },
    [queryClient]
  );

  const handleModalSuccess = () => {
    setModalOpen(false);
    setEditingPermission(null);
    queryClient.invalidateQueries({ queryKey: ["role-permissions"] });
  };

  const handleModalCancel = () => {
    setModalOpen(false);
    setEditingPermission(null);
  };

  // Handle direct is_active toggle update
  const handleToggleActive = useCallback(
    async (permission: RolePermission, checked: boolean) => {
      try {
        const response = await DropdownService.manageActiveRecord({
          table: "permission",
          pk: permission.id,
          is_active: checked,
        });
        if (response?.status === 200) {
          notify.success(
            response.code ?? "Success",
            response.message ?? "Permission status updated successfully"
          );
          queryClient.invalidateQueries({ queryKey: ["role-permissions"] });
        } else {
          notify.error(
            response?.code ?? "Error",
            response?.message ?? "Failed to update permission status"
          );
        }
      } catch (error: any) {
        notify.error(error?.message ?? "Failed to update permission status");
      }
    },
    [queryClient]
  );

  // Define columns for the permissions table
  const columns: ProColumns<RolePermission>[] = useMemo(
    () => [
      {
        title: "Schema",
        dataIndex: "schema_name",
        key: "schema_name",
        render: (_: any, record: RolePermission) => (
          <span className="font-medium text-[#111827]">
            {getSchemaName(record.schema_name)}
          </span>
        ),
        sorter: true,
      },
      {
        title: "Role",
        dataIndex: "role",
        key: "role",
        render: (_: any, record: RolePermission) => {
          const roleName = record.role_name ?? getRoleName(record.role);
          return (
            <div>
              <div className="font-semibold text-[#111827]">{roleName}</div>
              <div className="mt-1">
                <Checkbox checked={record.only_if_created ?? false} disabled>
                  Only If Created:
                </Checkbox>
              </div>
            </div>
          );
        },
      },
      // {
      //   title: "Level",
      //   dataIndex: "level",
      //   key: "level",
      //   render: (_: any, record: RolePermission) => (
      //     <span className="text-[#627384]">{record.level ?? 0}</span>
      //   ),
      // },
      {
        title: "Is Active",
        dataIndex: "is_active",
        key: "is_active",
        width: 120,
        render: (_: any, record: RolePermission) => (
          <Switch
            checked={record.is_active ?? false}
            onChange={checked => handleToggleActive(record, checked)}
            size="small"
            style={{
              backgroundColor: record.is_active ? "#0c3055" : "", // Green when active
              borderColor: record.is_active ? "#0c3055" : "", // Green border when active
            }}
          />
        ),
      },
      {
        title: "Permissions",
        key: "permissions",
        width: 600,
        render: (_: any, record: RolePermission) => {
          const basicPermissions = [
            { key: "read", label: "Read", value: record.can_read },
            { key: "write", label: "Write", value: record.can_write },
            { key: "create", label: "Create", value: record.can_create },
            { key: "delete", label: "Delete", value: record.can_delete },
          ];

          const additionalPermissions = [
            { key: "select", label: "Select", value: record.can_select },
            { key: "print", label: "Print", value: record.can_print },
            { key: "export", label: "Export", value: record.can_export },
            { key: "import", label: "Import", value: record.can_import },
            { key: "email", label: "Email", value: record.can_email },
            { key: "report", label: "Report", value: record.can_report },
            { key: "share", label: "Share", value: record.can_share },
          ];

          return (
            <div className="space-y-3">
              <div>
                <div className="mb-2 text-xs font-semibold text-gray-600">
                  Basic Permissions:
                </div>
                <div className="flex flex-wrap gap-2">
                  {basicPermissions.map(perm => (
                    <Tag
                      key={perm.key}
                      color={perm.value ? "green" : "default"}
                      className="m-0"
                    >
                      {perm.label}
                    </Tag>
                  ))}
                </div>
              </div>
              <div>
                <div className="mb-2 text-xs font-semibold text-gray-600">
                  Additional Permissions:
                </div>
                <div className="flex flex-wrap gap-2">
                  {additionalPermissions.map(perm => (
                    <Tag
                      key={perm.key}
                      color={perm.value ? "green" : "default"}
                      className="m-0"
                    >
                      {perm.label}
                    </Tag>
                  ))}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        title: "Actions",
        width: 120,
        valueType: "option",
        key: "actions",
        fixed: "right",
        render: (_: any, record: RolePermission) => {
          return [
            <CustomButton
              key="edit"
              icon={<Edit2 size={16} />}
              type="text"
              onClick={() => handleEditPermission(record)}
              title="Edit Permission"
            />,
            <Popconfirm
              key="delete"
              title="Are you sure you want to delete this permission?"
              onConfirm={() => handleDeletePermission(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <CustomButton
                danger
                icon={<Trash size={16} />}
                type="text"
                title="Delete Permission"
              />
            </Popconfirm>,
          ];
        },
      },
    ],
    [
      getRoleName,
      getSchemaName,
      handleEditPermission,
      handleDeletePermission,
      handleToggleActive,
    ]
  );

  // Filter permissions based on selected filters
  const permissions = useMemo(() => {
    let filtered = permissionsResponse?.results ?? [];

    if (selectedRoleFilter) {
      filtered = filtered.filter(
        (perm: RolePermission) =>
          perm.role === selectedRoleFilter ||
          perm.role_name === selectedRoleFilter
      );
    }

    if (selectedSchemaFilter) {
      filtered = filtered.filter(
        (perm: RolePermission) => perm.schema_name === selectedSchemaFilter
      );
    }

    return filtered;
  }, [permissionsResponse, selectedRoleFilter, selectedSchemaFilter]);

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      {/* Page Header */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
            Role Permission Manager
          </h1>
          <p className="text-sm text-[#627384]">
            Manage role permissions for schemas
          </p>
        </div>
        <Button
          onClick={handleAddPermission}
          className="bg-primary hover:bg-primary/90 flex items-center gap-2 whitespace-nowrap text-white"
        >
          <Plus className="h-4 w-4" />
          Create
        </Button>
      </div>

      {/* Filter Dropdowns */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-3 md:p-4">
          <div className="mb-4 flex flex-wrap items-end gap-4">
            <div className="min-w-[200px] flex-1">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Role
              </label>
              <Select
                placeholder="Select Role"
                allowClear
                showSearch
                filterOption={filterByLabel}
                value={selectedRoleFilter}
                onChange={value => setSelectedRoleFilter(value)}
                options={roleOptions}
                style={{ width: "100%" }}
                className="w-full"
              />
            </div>
            <div className="min-w-[200px] flex-1">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Schema
              </label>
              <Select
                placeholder="Select Schema"
                allowClear
                showSearch
                filterOption={filterByLabel}
                value={selectedSchemaFilter}
                onChange={value => setSelectedSchemaFilter(value)}
                options={schemaOptions}
                style={{ width: "100%" }}
                className="w-full"
              />
            </div>
            {(selectedRoleFilter ?? selectedSchemaFilter) && (
              <Button
                onClick={() => {
                  setSelectedRoleFilter(undefined);
                  setSelectedSchemaFilter(undefined);
                }}
                variant="outline"
                className="whitespace-nowrap"
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Permissions Table */}
      <Card className="border border-gray-200 bg-white">
        <CardContent className="p-3 md:p-4">
          <ProTable<RolePermission>
            rowKey="id"
            loading={permissionsLoading}
            columns={columns}
            dataSource={permissions}
            search={false}
            toolBarRender={() => []}
            rowSelection={{
              selectedRowKeys,
              onChange: keys => setSelectedRowKeys(keys),
            }}
            scroll={{ x: "max-content" }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: total => `Total ${total} items`,
            }}
          />
        </CardContent>
      </Card>

      {/* Permission Modal */}
      <RolePermissionModal
        open={modalOpen}
        onCancel={handleModalCancel}
        onSuccess={handleModalSuccess}
        initialData={editingPermission}
        isEditMode={!!editingPermission}
      />
    </div>
  );
};

export default RolePermissionManager;
